import { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { addEntry, listEntriesNewestFirst, removeEntry, subscribe } from './gallery';
import { apiEnabled } from './api';
import * as remote from './remoteGallery';

// React's view of the gallery — and, since Block I, of WHICH gallery.
//
// Two stores sit behind this one hook:
//   - anonymous: localStorage (src/gallery.js), synchronous, zero backend
//   - signed in: D1 via /api/gallery (src/remoteGallery.js), asynchronous
//
// Callers are not told which. `entries`, `save`, `remove` and `isSaved` mean the
// same thing either way; the only visible change is that save/remove are now
// PROMISES, because one of the two stores is a network away. They resolve to the
// same `{ ok }` / boolean the local store always returned.
//
// The switch is `isAuthenticated && apiEnabled`. That second term matters: a
// build without REACT_APP_AUTH0_AUDIENCE cannot obtain a verifiable token (see
// api.js), so it stays entirely on the Block H behaviour instead of failing
// every save. It means a missing build variable degrades to "the backend isn't
// switched on here" rather than to a broken gallery on the live domain.
const useGallery = () => {

    const { isAuthenticated, isLoading, user, getAccessTokenSilently } = useAuth0();
    const sub = user?.sub;
    const useRemote = Boolean(apiEnabled && isAuthenticated && sub);

    const [localEntries, setLocalEntries] = useState(listEntriesNewestFirst);
    const [remoteState, setRemoteState] = useState(remote.getState);

    useEffect(() => {
        const reload = () => setLocalEntries(listEntriesNewestFirst());
        // Re-read once on mount too: another tab may have written while this
        // component was unmounted.
        reload();
        return subscribe(reload);
    }, []);

    useEffect(() => remote.subscribe(() => setRemoteState(remote.getState())), []);

    // Wait for Auth0 to settle before deciding. Acting while `isLoading` would
    // tear the anonymous store down and rebuild it on every page load for a
    // signed-in user, and the gallery would flash empty in between.
    useEffect(() => {
        if (isLoading) return;
        if (useRemote) remote.setSession(sub, getAccessTokenSilently);
        else remote.clearSession();
    }, [isLoading, useRemote, sub, getAccessTokenSilently]);

    const entries = useRemote ? remoteState.entries : localEntries;

    const save = useCallback(
        (bounds, options) => (useRemote
            ? remote.addEntry(bounds, options)
            : Promise.resolve(addEntry(bounds, options))),
        [useRemote]
    );

    const remove = useCallback(
        (id) => (useRemote ? remote.removeEntry(id) : Promise.resolve(removeEntry(id))),
        [useRemote]
    );

    const isSaved = useCallback((id) => entries.some(e => e.id === id), [entries]);

    return {
        entries,
        save,
        remove,
        isSaved,
        // True only while the signed-in gallery is on its way. The local store is
        // synchronous, so anonymous visitors are never "loading" — the gallery's
        // empty state must not be shown to someone whose specimens are in flight.
        loading: useRemote && remoteState.status === 'loading',
        failed: useRemote && remoteState.status === 'error',
        retry: remote.reload,
        // Which store answered — for copy, not for logic. "Saved in this browser"
        // and "saved to your account" are different promises, and the empty state
        // should not make the wrong one.
        source: useRemote ? 'account' : 'device'
    };
};

export default useGallery;
